import { ReactNode, useRef } from 'react';
import ComboBoxField from '@/components/select/ComboBoxField';
import { SingleComboBoxProps } from '@/components/select/comboBoxTypes';
import useComboBoxQuery from '@/components/select/useComboBoxQuery';
import useSingleSelectBox from '@/components/select/useSingleSelectBox';
import { Combobox } from '@headlessui/react';

const SingleComboBox = <T extends { id: string; name: string }>({
  name,
  value,
  label,
  hideLabel,
  placeholder,
  queryKey = name ? [name] : [],
  queryFn,
  onSelect,
  allowEmptySelection = true,
  requireSelection,
  disabled = false,
  delay = 500,
  closeOnBlur = true,
  openOnFocus = true,
  showEmptyState = false,
}: SingleComboBoxProps<T>): ReactNode => {
  const isInitializedRef = useRef(false);
  const initialQuery = typeof value === 'string' ? value : (value?.name ?? '');
  const comboBox = useComboBoxQuery<T>({
    queryKey,
    queryFn,
    initialQuery,
    isInitializedRef,
    delay,
    closeOnBlur,
    openOnFocus,
  });
  const selection = useSingleSelectBox<T>(comboBox.items, { value, onSelect });
  isInitializedRef.current = selection.isInitialized;

  return (
    <Combobox<T | null, false>
      value={selection.selected}
      onChange={selection.handleChange}
      disabled={disabled}
      multiple={false}
      immediate={openOnFocus}
    >
      {({ open }) => (
        <ComboBoxField<T>
          label={label}
          hideLabel={hideLabel}
          placeholder={placeholder}
          inputRef={comboBox.inputRef}
          open={open}
          items={comboBox.items}
          isLoading={comboBox.isLoading}
          allowEmptySelection={allowEmptySelection}
          requireSelection={requireSelection}
          showEmptyOption
          displayValue={(item) => (!Array.isArray(item) && item ? item.name : '')}
          onFocus={comboBox.handleFocus}
          onBlur={comboBox.handleBlur}
          onAfterLeave={comboBox.handleAfterLeave}
          onDeselect={selection.handleDeselect}
          showEmptyState={showEmptyState}
        />
      )}
    </Combobox>
  );
};

export default SingleComboBox;
